import type { ClientDto } from '@fos/shared/contracts/crm';
import type { ClientPortalInvitation, ClientPortalPerson } from '@fos/shared/contracts/portal';
import { Mail, MoreHorizontal, RotateCw, UserPlus, UserRound, UserX, X } from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { appErrorCode, errorMessage, validationIssues } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDateShort, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button, IconButton } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { Field } from '../../../ui/Field';
import { Input } from '../../../ui/Input';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../ui/Menu';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { EmptyState, ErrorState, ListSkeleton } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON, TOUCH_ICON } from '../../mobile';

/**
 * Sekcja „Portal klienta” w Przeglądzie profilu klienta (DECISIONS 2026-10-17 [P] „Model portalu klienta” nr 7):
 * osoby klienta z dostępem do portalu zgłoszeń, czekające zaproszenia, „Zaproś do portalu” (kontakt klienta albo
 * adres i imię → mail z linkiem → konto → portal) i „Cofnij dostęp”. Tylko Właściciel i Admin (zarządzanie osobami;
 * Manager dostaje FORBIDDEN, więc sekcji nie widzi). Zmiany wymagają potwierdzenia hasłem (step-up — modal z
 * `stepUpLink`). Klient w archiwum: lista bez zapraszania.
 */
export function ClientPortalSection({ client }: { client: ClientDto }) {
  const access = useAccess();
  if (!access.isAdmin || client.partial) return null;
  return <PortalAccess client={client} />;
}

function PortalAccess({ client }: { client: ClientDto }) {
  const utils = trpc.useUtils();
  const portal = trpc.clients.portal.list.useQuery({ clientId: client.id });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [reinvite, setReinvite] = useState<{ email: string; name: string } | null>(null);
  const archived = client.status === 'archived' || client.archivedAt !== null;

  // anulowane potwierdzenie hasłem (step-up) to nie błąd
  const showError = (err: unknown) => {
    if (appErrorCode(err) !== 'STEP_UP_REQUIRED') toast.error(errorMessage(err));
  };
  const refresh = () => {
    void utils.clients.portal.list.invalidate({ clientId: client.id });
    // kolumna „Zgłoszenia od klientów” pojawia się, gdy workspace ma choć jedną osobę w portalu
    void utils.tasks.board.invalidate();
  };
  const resend = trpc.clients.portal.resendInvitation.useMutation({
    onSuccess: () => {
      toast.success('Wysłano zaproszenie ponownie');
      refresh();
    },
    onError: showError,
  });
  const cancel = trpc.clients.portal.cancelInvitation.useMutation({
    onSuccess: () => {
      toast.success('Zaproszenie anulowane');
      refresh();
    },
    onError: showError,
  });
  const revoke = trpc.clients.portal.revoke.useMutation({
    onSuccess: () => {
      toast.success('Dostęp cofnięty', { description: 'Osoba nie zaloguje się już do portalu.' });
      refresh();
    },
    onError: showError,
  });

  if (portal.isError && appErrorCode(portal.error) === 'FORBIDDEN') return null;

  const data = portal.data;
  const canManage = Boolean(data?.canManage) && !archived;
  const people = data?.people ?? [];
  const invitations = data?.invitations ?? [];
  const empty = people.length === 0 && invitations.length === 0;

  return (
    <Section aria-labelledby="client-portal">
      <SectionHeader
        title={<span id="client-portal">Portal klienta</span>}
        tone="strong"
        action={
          canManage ? (
            <Button
              variant="ghost"
              size="s"
              icon={UserPlus}
              className={TOUCH_BUTTON}
              onClick={() => setInviteOpen(true)}
            >
              Zaproś do portalu
            </Button>
          ) : undefined
        }
      />
      <p className="mb-3 text-caption text-fg-muted">
        Osoby klienta zgłaszają tu zadania i widzą ich status oraz rozmowę. Nie widzą czasu, kwot ani innych zadań.
      </p>
      {portal.isPending ? (
        <ListSkeleton rows={2} />
      ) : portal.isError ? (
        <ErrorState compact onRetry={() => void portal.refetch()} retrying={portal.isFetching} />
      ) : empty ? (
        <EmptyState
          compact
          icon={UserRound}
          title="Nikt z klienta nie ma jeszcze dostępu"
          description={canManage ? 'Zaproś osobę kontaktową — dostanie mail z linkiem do założenia konta.' : undefined}
        />
      ) : (
        <ul className="border-t border-line" aria-label="Osoby w portalu klienta">
          {people.map((p) => (
            <PersonRow
              key={p.membershipId}
              person={p}
              canManage={canManage}
              onRevoke={() => revoke.mutate({ membershipId: p.membershipId })}
              onReinvite={() => setReinvite({ email: p.email, name: p.name })}
            />
          ))}
          {invitations.map((inv) => (
            <InvitationRow
              key={inv.id}
              invitation={inv}
              canManage={canManage}
              onResend={() => resend.mutate({ invitationId: inv.id })}
              onCancel={() => cancel.mutate({ invitationId: inv.id })}
            />
          ))}
        </ul>
      )}
      {canManage && (
        <InviteToPortalDialog
          client={client}
          open={inviteOpen || reinvite !== null}
          prefill={reinvite}
          onOpenChange={(open) => {
            if (!open) {
              setInviteOpen(false);
              setReinvite(null);
            }
          }}
          onInvited={refresh}
        />
      )}
    </Section>
  );
}

function PersonRow({
  person,
  canManage,
  onRevoke,
  onReinvite,
}: {
  person: ClientPortalPerson;
  canManage: boolean;
  onRevoke: () => void;
  onReinvite: () => void;
}) {
  const disabled = person.status === 'disabled';
  const details = [
    person.email,
    disabled
      ? 'dostęp cofnięty'
      : person.lastLoginAt
        ? `ostatnio ${formatRelativePast(person.lastLoginAt)}`
        : 'jeszcze się nie logował(a)',
  ].join(' · ');
  return (
    <li className="flex items-center gap-3 border-b border-line py-3">
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface text-fg-muted">
        <UserRound size={16} strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className={disabled ? 'truncate text-body-s text-fg-muted' : 'truncate text-body-s font-medium text-fg'}>
          {person.name}
        </p>
        <p className="truncate text-caption text-fg-muted">{details}</p>
      </div>
      {canManage && (
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              icon={MoreHorizontal}
              label={`Akcje: ${person.name}`}
              iconSize={16}
              className={cn('relative', TOUCH_ICON)}
            />
          </MenuTrigger>
          <MenuContent align="end">
            {disabled ? (
              <MenuItem icon={RotateCw} onSelect={onReinvite}>
                Zaproś ponownie
              </MenuItem>
            ) : (
              <MenuItem icon={UserX} tone="danger" onSelect={onRevoke}>
                Cofnij dostęp
              </MenuItem>
            )}
          </MenuContent>
        </Menu>
      )}
    </li>
  );
}

function InvitationRow({
  invitation,
  canManage,
  onResend,
  onCancel,
}: {
  invitation: ClientPortalInvitation;
  canManage: boolean;
  onResend: () => void;
  onCancel: () => void;
}) {
  const expired = invitation.status === 'expired';
  return (
    <li className="flex items-center gap-3 border-b border-line py-3">
      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-surface text-fg-muted">
        <Mail size={16} strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-s text-fg">{invitation.name ?? invitation.email}</p>
        <p className="truncate text-caption text-fg-muted">
          {[
            invitation.name ? invitation.email : null,
            expired ? 'zaproszenie wygasło' : `zaproszenie czeka do ${formatDateShort(invitation.expiresAt)}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>
      {canManage && (
        <Menu>
          <MenuTrigger asChild>
            <IconButton
              icon={MoreHorizontal}
              label={`Akcje zaproszenia: ${invitation.email}`}
              iconSize={16}
              className={cn('relative', TOUCH_ICON)}
            />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem icon={RotateCw} onSelect={onResend}>
              Wyślij ponownie
            </MenuItem>
            <MenuItem icon={X} onSelect={onCancel}>
              Anuluj zaproszenie
            </MenuItem>
          </MenuContent>
        </Menu>
      )}
    </li>
  );
}

type InviteMode = 'contact' | 'email';
type InviteErrors = Partial<Record<'contactId' | 'email' | 'name', string>>;

/** „Zaproś do portalu”: kontakt klienta (adres i imię z kontaktu) albo adres i imię podane ręcznie. */
export function InviteToPortalDialog({
  client,
  open,
  prefill,
  onOpenChange,
  onInvited,
}: {
  client: ClientDto;
  open: boolean;
  /** „Zaproś ponownie” osobę z cofniętym dostępem */
  prefill?: { email: string; name: string } | null;
  onOpenChange: (open: boolean) => void;
  onInvited?: () => void;
}) {
  const contacts = trpc.clients.contacts.list.useQuery({ clientId: client.id }, { enabled: open });
  const invite = trpc.clients.portal.invite.useMutation();
  const withEmail = useMemo(() => (contacts.data ?? []).filter((c) => !c.archivedAt && c.email), [contacts.data]);
  const [mode, setMode] = useState<InviteMode>('contact');
  const [contactId, setContactId] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<InviteErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setFormError(null);
    setContactId(undefined);
    setEmail(prefill?.email ?? '');
    setName(prefill?.name ?? '');
    setMode(prefill ? 'email' : 'contact');
  }, [open, prefill]);

  // bez kontaktów z adresem — od razu adres ręcznie
  const contactsReady = contacts.isSuccess;
  useEffect(() => {
    if (open && contactsReady && withEmail.length === 0) setMode('email');
  }, [open, contactsReady, withEmail.length]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const next: InviteErrors = {};
    if (mode === 'contact' && !contactId) next.contactId = 'Wybierz kontakt';
    if (mode === 'email') {
      if (!email.trim()) next.email = 'Podaj adres email';
      if (name.trim().length < 2) next.name = 'Podaj imię i nazwisko';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    try {
      await invite.mutateAsync(
        mode === 'contact'
          ? { clientId: client.id, contactId }
          : { clientId: client.id, email: email.trim(), name: name.trim() },
      );
      const target = mode === 'contact' ? withEmail.find((c) => c.id === contactId)?.email : email.trim();
      toast.success('Zaproszenie wysłane', {
        description: target ? `Mail z linkiem poszedł na ${target}.` : undefined,
      });
      onInvited?.();
      onOpenChange(false);
    } catch (err) {
      if (appErrorCode(err) === 'STEP_UP_REQUIRED') return; // anulowane potwierdzenie hasłem
      const mapped: InviteErrors = {};
      for (const issue of validationIssues(err)) {
        const key = issue.path as keyof InviteErrors;
        if ((key === 'contactId' || key === 'email' || key === 'name') && !mapped[key]) mapped[key] = issue.message;
      }
      if (mode === 'contact' && mapped.email && !mapped.contactId) {
        mapped.contactId = mapped.email;
        delete mapped.email;
      }
      if (Object.keys(mapped).length > 0) setErrors(mapped);
      else setFormError(errorMessage(err));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Zaproś do portalu"
      description={`Osoba dostanie mail z linkiem (ważny 7 dni). Po założeniu konta zobaczy tylko zgłoszenia klienta ${client.name}.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button type="submit" form="portal-invite-form" variant="primary" loading={invite.isPending}>
            Wyślij zaproszenie
          </Button>
        </>
      }
    >
      <form id="portal-invite-form" onSubmit={submit} noValidate className="flex flex-col gap-4">
        {withEmail.length > 0 && (
          <Segmented<InviteMode>
            aria-label="Kogo zaprosić"
            value={mode}
            onValueChange={setMode}
            options={[
              { value: 'contact', label: 'Kontakt klienta' },
              { value: 'email', label: 'Inny adres' },
            ]}
          />
        )}
        {mode === 'contact' ? (
          <Field label="Kontakt" error={errors.contactId}>
            <Select
              value={contactId}
              onValueChange={setContactId}
              placeholder={contacts.isPending ? 'Wczytywanie…' : 'Wybierz osobę'}
              options={withEmail.map((c) => ({
                value: c.id,
                label: `${c.name} · ${c.email}`,
              }))}
            />
          </Field>
        ) : (
          <>
            <Field label="Email" error={errors.email}>
              <Input
                type="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </Field>
            <Field label="Imię i nazwisko" hint="Podpowiemy je w formularzu konta." error={errors.name}>
              <Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
            </Field>
          </>
        )}
        {formError && <FormMessage>{formError}</FormMessage>}
      </form>
    </Dialog>
  );
}
